import React from "react";
import PropTypes from "prop-types";
import delay from "lodash/delay";
import humps from "humps";
import Spinner from "@paprika/spinner";
import Toast from "@paprika/toast";
import i18n from "@viz-ui/i18n/i18n";
import services from "@visualizer/common/services/appGlue";
import Utils from "@viz-ui/services/common/utilsService";
import EventService from "@viz-ui/services/eventService/eventService";

const DEFAULT_TIMEOUT = 5000;
const EXPORT_XLS_CELL_LIMIT_EXCEEDED_EVENT = "exportXls.cellLimitExceeded";
const DONE_STATE = "done";
const CELL_VALUE_EXCEEDED_STATE = "cell_value_exceeded";
const FAILED_STATE = "failed";
const TERMINAL_FAILURE_STATES = [CELL_VALUE_EXCEEDED_STATE, FAILED_STATE];
export const XLS_CELL_LIMIT_EXCEEDED_ERROR_KEY = "_Export.Xls.error.cell_limit_exceeded_";
const XLS_UNKNOWN_ERROR_KEY = "_Export.Xls.error.unknown_";

export default class DownloadXlsBody extends React.Component {
  static propTypes = {
    onStatusChange: PropTypes.func,
  };

  static defaultProps = {
    onStatusChange: undefined,
  };

  state = {
    isRunning: true,
    submitHref: null,
    hasError: false,
    errorMessageKey: XLS_UNKNOWN_ERROR_KEY,
  };

  componentDidMount() {
    this._isMounted = true;
    this.xlsUrl = services.AppConfig.exportToXlsUrl;
    this.docParams = {
      projectId: services.AppConfig.project_id,
      controlId: services.AppConfig.control_id,
      controlTestId: services.AppConfig.control_test_id,
    };
    this.notifyStatusChange();
    this.checkDownloadSource();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  getErrorMessageKeyForState(state) {
    return state === CELL_VALUE_EXCEEDED_STATE ? XLS_CELL_LIMIT_EXCEEDED_ERROR_KEY : XLS_UNKNOWN_ERROR_KEY;
  }

  getHeaders() {
    let headers = {
      "Content-type": "application/json",
      "X-Requested-By": "visualizer",
    };

    if (services.window._token) {
      headers = Object.assign(headers, { "X-CSRF-TOKEN": services.window._token });
    }

    return headers;
  }

  getLocalizedErrorMessage(key) {
    const fromI18n = i18n.t(key);
    if (fromI18n !== key) {
      return fromI18n;
    }
    return services.Localize.getLocalizedString(key);
  }

  getUrl(attachment, type) {
    const { id, attachable_id: attachableId, attachable_type: attachableType } = attachment;
    return `/download_attachments/${id}.${type}?download_attachment[attachable_id]=${attachableId}&download_attachment[attachable_type]=${attachableType}`;
  }

  notifyStatusChange() {
    const { onStatusChange } = this.props;
    if (onStatusChange) {
      onStatusChange({
        isRunning: this.state.isRunning,
        hasError: this.state.hasError,
        errorMessageKey: this.state.errorMessageKey,
      });
    }
  }

  checkDownloadSource() {
    this.startAttachmentGenerator(this.docParams).then(attachment => this.checkIfAttachmentReady(attachment));
  }

  checkIfAttachmentReady(attachment) {
    this.pollForAttachmentReadiness(attachment).then(response => {
      if (this._isMounted) {
        if (!response) {
          delay(() => {
            this.checkIfAttachmentReady(attachment);
          }, DEFAULT_TIMEOUT);
          return;
        }

        const { state } = response;
        if (state === DONE_STATE) {
          this.updateDownloadLink(this.getUrl(attachment, "xls"));
        } else if (this.isTerminalFailureState(state)) {
          this.markAsFailed(state);
        } else {
          delay(() => {
            this.checkIfAttachmentReady(response);
          }, DEFAULT_TIMEOUT);
        }
      }
    });
  }

  isTerminalFailureState(state) {
    return TERMINAL_FAILURE_STATES.includes(state);
  }

  markAsFailed(state) {
    if (state === CELL_VALUE_EXCEEDED_STATE) {
      EventService.register("downloadXlsModal").publish(EXPORT_XLS_CELL_LIMIT_EXCEEDED_EVENT);
    }
    this.setState(
      {
        isRunning: false,
        submitHref: null,
        hasError: true,
        errorMessageKey: this.getErrorMessageKeyForState(state),
      },
      () => this.notifyStatusChange()
    );
  }

  async pollForAttachmentReadiness(attachment) {
    try {
      const response = await fetch(this.getUrl(attachment, "json"), {
        credentials: "include", // required to include cookies
        headers: this.getHeaders(),
        method: "GET",
      });
      return await response.json();
    } catch (err) {
      Utils.handleError(err, "Xls");
    }
  }

  async startAttachmentGenerator(params) {
    const { projectId, controlId, controlTestId } = humps.camelizeKeys(params);

    var data = {
      file_name: Utils.generateFilename("xls"),
      export_data: JSON.stringify(services.DataModel.toSaveViz()),
    };

    const url = `/projects/${projectId}/controls/${controlId}/control_tests/${controlTestId}/interpretations/export_xls`;

    try {
      const response = await fetch(url, {
        credentials: "include", // required to include cookies
        body: JSON.stringify(data), // must match 'Content-Type' header
        headers: this.getHeaders(),
        method: "POST",
      });
      return await response.json();
    } catch (err) {
      Utils.handleError(err, "Xls");
    }
  }

  updateDownloadLink(url) {
    this.setState({ isRunning: false, submitHref: url, hasError: false }, () => this.notifyStatusChange());
  }

  renderContent() {
    if (this.state.isRunning) {
      return <Spinner caption={services.Localize.getLocalizedString("_Export.Xls.processing_")} />;
    }

    if (this.state.hasError) {
      return (
        <p className="processing-message processing-message--error">
          {this.getLocalizedErrorMessage(this.state.errorMessageKey)}
        </p>
      );
    }

    return (
      <>
        <Toast hasCloseButton={false} canAutoClose autoCloseDelay={1500} kind="visually-hidden">
          {services.Localize.getLocalizedString("_Export.Xls.readyMessage_")}
        </Toast>
        <p className="processing-message">
          <a href={this.state.submitHref} className="download-message">
            {services.Localize.getLocalizedString("_Export.Xls.ready_")}
          </a>
        </p>
      </>
    );
  }

  render() {
    return <div className="download-xls-modal__content">{this.renderContent()}</div>;
  }
}
